import { useState } from "react";

import { useJob, useMigration, useRunDelta } from "../../api/hooks";
import { formatDateTime } from "../../api/types";
import { Button, Disclosure, EmptyState, Table, type Column } from "../../components";
import { t } from "../../i18n/t";

export interface DeltaRow {
  entity: string;
  added: number;
  changed: number;
  removed: number;
}

/** Status.html's delta table shows sentence-case entity names ("Bank transactions"), not raw keys. */
function entityLabel(entity: string): string {
  const spaced = entity.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function counts(value: unknown): DeltaRow[] {
  if (typeof value !== "object" || value === null) return [];
  return Object.entries(value as Record<string, unknown>).map(([entity, raw]) => {
    const row = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
    return {
      entity,
      added: typeof row.added === "number" ? row.added : 0,
      changed: typeof row.changed === "number" ? row.changed : 0,
      removed: typeof row.removed === "number" ? row.removed : 0,
    };
  });
}

/**
 * §B6.2. `runtime["delta"]` per entity, the `hypercare_until` date, and a "now" button — the sweep
 * (`tasks.hypercare_delta_sweep`) already runs every six hours, and the panel says so in one sentence so an
 * operator knows the button is a "now", not the only path.
 */
export function DeltaPanel({ migrationId }: { migrationId: string }): JSX.Element {
  const migration = useMigration(migrationId);
  const runDelta = useRunDelta(migrationId);
  // `JobOut.job_id` is a procrastinate BIGINT, so the state and `useJob` are both numeric.
  const [jobId, setJobId] = useState<number | null>(null);
  const [accepted, setAccepted] = useState(false);
  const job = useJob(jobId);

  // §B6.2's `runtime["delta"]`, promoted to a top-level `delta` field on `MigrationOut` rather than
  // exposing the whole runtime blob (which also holds the scorecard, the manifest and the capabilities).
  const rows = counts(migration.data?.delta);

  /**
   * (v3-M-8) A future `scheduled_at` on the job row is `DailyCapReached` surfacing as information: the task
   * rescheduled itself at the vendor's refill time and **succeeded** (§A9.4). A spinner here would render
   * "nothing is happening" for something with a known resume time.
   */
  const scheduledAt = job.data?.scheduled_at ?? null;
  const rescheduled = scheduledAt !== null && Date.parse(scheduledAt) > Date.now();

  const columns: readonly Column<DeltaRow>[] = [
    { key: "entity", header: t("delta.col_entity"), render: (row) => entityLabel(row.entity) },
    {
      key: "added",
      header: t("delta.col_added"),
      numeric: true,
      render: (row) => <span data-testid={`delta-added-${row.entity}`}>{row.added}</span>,
    },
    {
      key: "changed",
      header: t("delta.col_changed"),
      numeric: true,
      render: (row) => <span data-testid={`delta-changed-${row.entity}`}>{row.changed}</span>,
    },
    {
      key: "removed",
      header: t("delta.col_removed"),
      numeric: true,
      render: (row) => <span data-testid={`delta-removed-${row.entity}`}>{row.removed}</span>,
    },
  ];

  const sum = (key: "added" | "changed" | "removed"): number =>
    rows.reduce((total, row) => total + row[key], 0);
  const counts_ =
    rows.length === 0
      ? t("delta.counts_none")
      : t("delta.counts", { added: sum("added"), changed: sum("changed"), removed: sum("removed") });

  return (
    <Disclosure asHeading title={t("status.deltas_title")} counts={counts_} testId="status-deltas-section">
      <p data-testid="delta-hypercare-until">
        {t("delta.until", { date: migration.data?.hypercare_until ?? t("common.unknown") })}
      </p>
      <p data-testid="delta-sweep">{t("delta.sweep")}</p>

      {rows.length === 0 ? (
        <EmptyState title={t("delta.empty")} />
      ) : (
        <Table
          caption={t("status.deltas_title")}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.entity}
          emptyMessage={t("delta.empty")}
        />
      )}

      <Button
        disabled={runDelta.isPending}
        onClick={() =>
          void runDelta.mutateAsync({}).then((accepted_) => {
            // (v3-H-1) `job_id: null` is a legal, permanent SUCCESS — the job is already queued, or the
            // idempotency record completed before the defer. It is never an error.
            setJobId(accepted_.job_id ?? null);
            setAccepted(true);
          })
        }
      >
        {runDelta.isPending ? t("common.loading") : t("delta.run_now")}
      </Button>
      {accepted && !rescheduled ? <p data-testid="delta-accepted">{t("delta.accepted")}</p> : null}
      {rescheduled ? (
        <p data-testid="delta-rescheduled">{t("delta.capped", { at: formatDateTime(scheduledAt) })}</p>
      ) : null}
    </Disclosure>
  );
}
